import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Search,
  LogOut,
  Phone,
  Mail,
  MessageCircle,
  RefreshCw,
  CheckCircle,
  Clock,
  Archive,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Aurora } from "../components/Background";
import useSEO from "../hooks/useSEO";

const STATUS_OPTIONS = [
  { value: "new", label: "New", color: "#27C6FF" },
  { value: "contacted", label: "Contacted", color: "#CFAEFF" },
  { value: "converted", label: "Converted", color: "#7CFFC4" },
  { value: "archived", label: "Archived", color: "#9B86C8" },
];

const StatChip = ({ label, value, accent }) => (
  <div className="glass rounded-2xl p-5">
    <div className="text-[#CFAEFF] text-xs font-heading uppercase tracking-wider">
      {label}
    </div>
    <div
      className="font-display font-bold text-3xl mt-2"
      style={{ color: accent }}
      data-testid={`stat-${label.toLowerCase()}`}
    >
      {value}
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const opt = STATUS_OPTIONS.find((o) => o.value === status) || STATUS_OPTIONS[0];
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-heading font-semibold uppercase tracking-wider"
      style={{
        background: `${opt.color}1A`,
        color: opt.color,
        border: `1px solid ${opt.color}66`,
      }}
    >
      {opt.label}
    </span>
  );
};

export default function AdminDashboard() {
  const { user, logout, authHeader, API } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  useSEO({
    title: "Leads · DietDotIn Admin",
    description: "Consultation lead management for DietDotIn.",
    noindex: true,
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers: authHeader() }),
        axios.get(`${API}/admin/stats`, { headers: authHeader() }),
      ]);
      setLeads(l.data);
      setStats(s.data);
    } catch (err) {
      toast.error("Couldn't load admin data");
    } finally {
      setLoading(false);
    }
  }, [API, authHeader]);

  useEffect(() => {
    if (user && user.role === "admin") fetchAll();
  }, [user, fetchAll]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return leads.filter((l) => {
      if (filter !== "all" && (l.status || "new") !== filter) return false;
      if (!term) return true;
      return (
        (l.name || "").toLowerCase().includes(term) ||
        (l.phone || "").toLowerCase().includes(term) ||
        (l.email || "").toLowerCase().includes(term) ||
        (l.goal || "").toLowerCase().includes(term) ||
        (l.message || "").toLowerCase().includes(term)
      );
    });
  }, [leads, q, filter]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/leads/${id}`,
        { status },
        { headers: authHeader() },
      );
      setLeads((curr) =>
        curr.map((l) => (l.id === id ? { ...l, status } : l)),
      );
      // Refresh stats in background
      axios
        .get(`${API}/admin/stats`, { headers: authHeader() })
        .then((r) => setStats(r.data))
        .catch(() => {});
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Couldn't update status");
    }
  };

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#CFAEFF]">
        Loading…
      </div>
    );
  }
  if (!user || user.role !== "admin") {
    return <Navigate to="/admin/login" replace />;
  }

  const fmt = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso || "";
    }
  };

  return (
    <div className="relative min-h-screen pb-20">
      <Aurora />

      {/* Header bar */}
      <div className="sticky top-0 z-40 backdrop-blur-xl bg-[#140326]/75 border-b border-[#B48DFF]/15">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img
              src="/brand/logo.png?v=4"
              alt="DietDotIn"
              className="h-10 w-auto drop-shadow-[0_0_14px_rgba(122,43,255,0.5)]"
            />
            <div className="hidden md:block">
              <div className="text-white font-heading font-semibold text-sm">
                Admin Console
              </div>
              <div className="text-[#CFAEFF] text-[11px]">
                {user.email}
              </div>
            </div>
          </a>
          <div className="flex items-center gap-3">
            <a
              href="/admin/diet-plans"
              data-testid="admin-diet-plans-link"
              className="btn-ghost-glass px-4 py-2 rounded-full text-xs font-heading inline-flex items-center gap-2"
            >
              Diet Plans
            </a>
            <button
              onClick={fetchAll}
              className="btn-ghost-glass px-4 py-2 rounded-full text-xs font-heading inline-flex items-center gap-2"
              data-testid="admin-refresh"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={logout}
              className="btn-primary px-4 py-2 rounded-full text-xs font-heading inline-flex items-center gap-2"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 pt-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="font-display font-extrabold text-3xl md:text-4xl text-white">
            Consultation <span className="gradient-text">Leads</span>
          </h1>
          <p className="mt-2 text-[#D8CCFF] text-sm">
            Every enquiry from the landing page lives here. Triage, contact and convert.
          </p>
        </motion.div>

        {/* Stats */}
        {stats && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatChip label="Total Leads" value={stats.total} accent="#F5F3FF" />
            <StatChip label="New" value={stats.new} accent="#27C6FF" />
            <StatChip label="Contacted" value={stats.contacted} accent="#CFAEFF" />
            <StatChip label="Converted" value={stats.converted} accent="#7CFFC4" />
          </div>
        )}

        {/* Top goals */}
        {stats?.top_goals?.length > 0 && (
          <div className="mt-6 glass rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={16} className="text-[#27C6FF]" />
              <div className="text-white font-heading font-semibold text-sm">
                Top Goals
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {stats.top_goals.map((g) => (
                <span
                  key={g.goal}
                  className="px-3 py-1.5 rounded-full text-xs glass text-[#F5F3FF]"
                  data-testid={`top-goal-${g.goal}`}
                >
                  {g.goal} <span className="text-[#27C6FF] ml-1">{g.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="mt-8 flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
          <div className="relative flex-1 max-w-xl">
            <Search
              size={16}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-[#CFAEFF]"
            />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, phone, email, goal…"
              className="field w-full pl-11 pr-4 py-3 rounded-xl text-sm"
              data-testid="admin-search"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {[{ value: "all", label: "All" }, ...STATUS_OPTIONS].map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                data-testid={`filter-${f.value}`}
                className={`px-4 py-2 rounded-full text-xs font-heading font-semibold transition-all ${
                  filter === f.value
                    ? "bg-[#7A2BFF] text-white border border-[#27C6FF]/60 shadow-[0_0_20px_rgba(122,43,255,0.45)]"
                    : "glass text-[#CFAEFF] hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Leads list */}
        <div className="mt-6 glass rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-[#CFAEFF]">Loading leads…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-[#CFAEFF]" data-testid="leads-empty">
              No leads match your filters.
            </div>
          ) : (
            <div className="divide-y divide-[#B48DFF]/10">
              {filtered.map((lead) => (
                <div
                  key={lead.id}
                  className="p-5 md:p-6 hover:bg-white/[0.025] transition-colors"
                  data-testid={`lead-row-${lead.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="font-heading font-semibold text-white text-base md:text-lg">
                          {lead.name}
                        </div>
                        <StatusBadge status={lead.status || "new"} />
                        <span className="text-[10px] text-[#9B86C8] inline-flex items-center gap-1">
                          <Clock size={11} /> {fmt(lead.created_at)}
                        </span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-[#CFAEFF]">
                        <a
                          href={`tel:${lead.phone}`}
                          className="inline-flex items-center gap-1.5 hover:text-white"
                        >
                          <Phone size={12} /> {lead.phone}
                        </a>
                        {lead.email && (
                          <a
                            href={`mailto:${lead.email}`}
                            className="inline-flex items-center gap-1.5 hover:text-white break-all"
                          >
                            <Mail size={12} /> {lead.email}
                          </a>
                        )}
                        {lead.goal && (
                          <span className="inline-flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#27C6FF]" />
                            {lead.goal}
                          </span>
                        )}
                      </div>
                      {lead.message && (
                        <p className="mt-3 text-sm text-[#D8CCFF] leading-relaxed max-w-2xl">
                          “{lead.message}”
                        </p>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <a
                        href={`https://wa.me/${(lead.phone || "").replace(/\D/g, "")}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#25D366] text-white text-xs font-heading font-semibold hover:shadow-[0_0_24px_rgba(37,211,102,0.5)]"
                        data-testid={`lead-whatsapp-${lead.id}`}
                      >
                        <MessageCircle size={12} /> WhatsApp
                      </a>
                      {(lead.status || "new") !== "contacted" && (
                        <button
                          onClick={() => updateStatus(lead.id, "contacted")}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full glass text-[#CFAEFF] hover:text-white text-xs font-heading"
                          data-testid={`mark-contacted-${lead.id}`}
                        >
                          Contacted
                        </button>
                      )}
                      {(lead.status || "new") !== "converted" && (
                        <button
                          onClick={() => updateStatus(lead.id, "converted")}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#7CFFC4]/15 border border-[#7CFFC4]/40 text-[#7CFFC4] hover:text-white hover:bg-[#7CFFC4]/25 text-xs font-heading"
                          data-testid={`mark-converted-${lead.id}`}
                        >
                          <CheckCircle size={12} /> Converted
                        </button>
                      )}
                      {(lead.status || "new") !== "archived" && (
                        <button
                          onClick={() => updateStatus(lead.id, "archived")}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full glass text-[#9B86C8] hover:text-white text-xs font-heading"
                          data-testid={`archive-${lead.id}`}
                        >
                          <Archive size={12} /> Archive
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
